import React, { type ReactNode, useMemo, useState } from 'react';
import type {
  CompaniesFilterChange,
  FilterChange,
  SortFilterChange,
} from '@/app/components/hotel-detail/changeTransport/ChangeTransportFiltersCommon';
import { useTransportChangeFilters } from '@/app/components/hotel-detail/changeTransport/ChangeTransportFiltersProvider';
import { useCurrentFilterStatus } from '@/app/components/hotel-detail/CurrentFilterStatusProvider';
import { useCuriosity } from '@/app/components/hotel-detail/CuriosityProvider';
import { useTranslation } from '@/app/components/translations/Translation';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { IconSorting } from '@/app/components/hotel-detail/common/icons';
import {
  getSortOptionName,
  sortEventOriginMap,
  sortOptionsMap,
} from '@/app/components/hotel-detail/changeTransport/ChangeTransportSorting';
import { EvoAirlinesFilter } from '@/app/components/hotel-detail/evo/transport/EvoTransportFilters/filters/EvoAirlinesFilter';
import { EvoAirportsFilter } from '@/app/components/hotel-detail/evo/transport/EvoTransportFilters/filters/EvoAirportsFilter';
import { EvoBaggageFilter } from '@/app/components/hotel-detail/evo/transport/EvoTransportFilters/filters/EvoBaggageFilter';
import { EvoSortFilter } from '@/app/components/hotel-detail/evo/transport/EvoTransportFilters/filters/EvoSortFilter';
import { EvoStopsFilter } from '@/app/components/hotel-detail/evo/transport/EvoTransportFilters/filters/EvoStopsFilter';
import { EvoTimeSlotFilter } from '@/app/components/hotel-detail/evo/transport/EvoTransportFilters/filters/EvoTimeSlotFilter';
import {
  buildEvoStopsNewState,
  deriveEvoStopsOption,
} from '@/app/components/hotel-detail/evo/transport/EvoTransportFilters/filters/evoStopsFilter.utils';
import type { EvoTransportPillProps } from '@/app/components/hotel-detail/evo/transport/EvoTransportFilters/EvoTransportPill';
import { EvoTransportPill } from '@/app/components/hotel-detail/evo/transport/EvoTransportFilters/EvoTransportPill';
import type { EvoDraftFilterChange } from '@/app/components/hotel-detail/evo/transport/EvoTransportFilters/evoDraftFilterChange';
import { mergeEvoDraftFilterChange } from '@/app/components/hotel-detail/evo/transport/EvoTransportFilters/evoDraftFilterChange';
import styled from 'styled-components';
import ScrollContainer from '@/app/common/components/ScrollContainer';
import { EVO_BODY_INSET, EVO_BODY_INSET_MEDIA } from '@/app/components/search/components/Modal/OpenXModal/utils';

// #region EvoTransportPill Styles
/* MPH-1050: the scroll viewport spans the full width of its container — on mobile that is the
   whole modal, because EvoTransportPageLayout's sticky header bleeds out of the modal body inset —
   and the inset is re-applied to the scrolling content instead. That way the first and last pill
   line up with the flight cards below while the row still scrolls to both modal edges, rather than
   being clipped short of them. From the same breakpoint up (tablet and desktop) the header keeps
   its own 24px inset, so the row only needs the 2px that stops the end pills' box-shadow being
   clipped by the scroll container. */
const PillsRow = styled.div`
  --evo-pills-inset: 2px;
  width: 100%;

  @media ${EVO_BODY_INSET_MEDIA} {
    --evo-pills-inset: ${EVO_BODY_INSET};
  }
`;

const SortPillSeparator = styled.div`
  width: 1px;
  height: 24px;
  flex-shrink: 0;
  margin-right: 10px;
  background-color: ${({ theme }) => theme.kallisto.color.border.static.borderNeutral};
`;

// #endregion EvoTransportPill Styles

export type FilterPillIdType = 'sort' | 'baggage' | 'stops' | 'times' | 'airports' | 'airlines';

// Whether a pill belongs on the bar is decided here and filtered on before rendering, so it is not
// something EvoTransportPill itself ever reads.
type PillCandidate = EvoTransportPillProps & { mustBeShown: boolean };

type FilterPillDefinition = {
  id: FilterPillIdType;
  textKey: string;
  icon?: React.ReactNode;
};

type EvoTransportFiltersProps = {
  isDesktop: boolean;
  // MPH-469: when true, render fixed non-interactive pills using this same layout while the
  // alternative flights are still loading, instead of deriving them from the (empty)
  // currentFiltersStatus. Keeps the filters row identical to the loaded state (no layout shift).
  isLoading?: boolean;
};

export function EvoTransportFilters(props: EvoTransportFiltersProps) {
  const { isDesktop, isLoading = false } = props;
  const { t } = useTranslation();
  const { currentFiltersStatus } = useCurrentFilterStatus();
  const { changeFilter } = useTransportChangeFilters();
  const { sendCuriosityClickEvent } = useCuriosity();
  const { localization } = usePageWrapperData();

  const currentSelectedSortOption = useMemo(() => {
    const selectedSF = currentFiltersStatus?.sortOptions?.find((sf) => sf.selected);
    return selectedSF && selectedSF.option ? sortOptionsMap[selectedSF.option] : undefined;
  }, [currentFiltersStatus?.sortOptions]);

  const filterPillDefinitions: FilterPillDefinition[] = [
    {
      id: 'sort',
      // MPH-398: the Evo mobile pill is just "Sort" (like the other pills); desktop keeps the selected label.
      textKey: isDesktop
        ? `${t('vacation.oss.change_transport.filters.sort_by')}: ${getSortOptionName({ sortOption: currentSelectedSortOption ?? 'BERTOLI', t, isTrain: false })}`
        : t('vacation.oss.components.searchresults.sort.title'),
      icon: <IconSorting opaque={true} />,
    },
    { id: 'baggage', textKey: t('vacation.oss.change_transport.filters.baggage') },
    { id: 'stops', textKey: t('vacation.common.filters.stops.title') },
    { id: 'times', textKey: t('vacation.flightavailability.filters.traveltimes') },
    { id: 'airports', textKey: t('vacation.flightavailability.filters.airports') },
    { id: 'airlines', textKey: t('vacation.oss.change_transport.filters.provider.airlines') },
  ];

  const [activeFilterModal, setActiveFilterModal] = useState<FilterPillIdType | null>(null);
  // Staged (not yet applied) changes of the open popover; applied as a single
  // changeFilter call on Apply, discarded on close without Apply.
  const [draftFilterChange, setDraftFilterChange] = useState<EvoDraftFilterChange | null>(null);

  const draftFiltersStatus = useMemo(
    () => (currentFiltersStatus ? { ...currentFiltersStatus, ...draftFilterChange?.newState } : undefined),
    [currentFiltersStatus, draftFilterChange]
  );

  const togglePillActivation = (filterId: FilterPillIdType) => {
    setDraftFilterChange(null);
    setActiveFilterModal(filterId === activeFilterModal ? null : filterId);
  };

  const stageFilterChange = (filterChangeValue: FilterChange) => {
    setDraftFilterChange((previousDraft) => mergeEvoDraftFilterChange(previousDraft, filterChangeValue));
  };

  const closeModal = () => {
    setActiveFilterModal(null);
    setDraftFilterChange(null);
  };

  // MPH-398: the "click_transport_sorting" curiosity event is sent here, when the sort is actually
  // applied, rather than on each staged radio tap — so staged-but-cancelled selections aren't reported.
  const sendSortCuriosityEvent = (sortFilterChange: SortFilterChange) => {
    const selectedOption = sortFilterChange.newState?.sortOptions?.find((sf) => sf.selected)?.option;
    if (!selectedOption) return;
    const sortOption = sortOptionsMap[selectedOption as keyof typeof sortOptionsMap];
    sendCuriosityClickEvent({
      event_name: 'click_transport_sorting',
      event_value:
        sortOption === 'PRICE_TRANSPORT' ? 'cheapest' : sortOption === 'FASTEST_TRANSPORT' ? 'fastest' : 'recommended',
    });
  };

  const applyDraftFilter = () => {
    if (draftFilterChange) {
      if (activeFilterModal === 'sort') sendSortCuriosityEvent(draftFilterChange as SortFilterChange);
      changeFilter(draftFilterChange as FilterChange);
    }
    closeModal();
  };

  // MPH-398: desktop sort stays immediate (MPH-259 popover, no footer); mobile sort is deferred
  // (staged via the footer "Apply"). The sort component is shared, so the behaviour is chosen here.
  const applySortImmediately = (filterChange: FilterChange) => {
    sendSortCuriosityEvent(filterChange as SortFilterChange);
    changeFilter(filterChange);
    closeModal();
  };

  const buildClearFilterChange = (filterId: FilterPillIdType, filtersStatus: TransportFiltersResponse) => {
    switch (filterId) {
      case 'sort': {
        if (!filtersStatus.sortOptions) return null;

        const popularSortTransportOption: TransportSortOption = 'BERTOLI';

        const newSortOptionsState = filtersStatus.sortOptions.map((sf) => ({
          ...sf,
          selected: sortOptionsMap[sf.option as keyof typeof sortOptionsMap] === popularSortTransportOption,
        }));

        return {
          newState: { sortOptions: newSortOptionsState },
          event: {
            type: 'SORT',
            origin: sortEventOriginMap[popularSortTransportOption],
          },
        } as SortFilterChange;
      }
      case 'baggage': {
        const newState = {
          baggageIncluded:
            filtersStatus.baggageIncluded?.map((baggage) => ({
              ...baggage,
              selected: false,
            })) || [],
          handBaggageIncluded:
            filtersStatus.handBaggageIncluded?.map((handBaggage) => ({
              ...handBaggage,
              selected: false,
            })) || [],
        };
        return {
          newState,
          event: { type: 'FILTER', origin: 'BAGGAGE_FILTER' },
        } as FilterChange;
      }
      case 'stops': {
        const newState = buildEvoStopsNewState('ANY', {
          outboundStops: filtersStatus.outboundStops,
          inboundStops: filtersStatus.inboundStops,
          directOnly: filtersStatus.directOnly,
        });
        return {
          newState,
          event: { type: 'FILTER', origin: 'STOPS_CLEAR_ALL_FILTER' },
        } as FilterChange;
      }
      case 'times': {
        const newState = {
          outboundDepartureTimeSlots:
            filtersStatus.outboundDepartureTimeSlots?.map((timeSlot) => ({
              ...timeSlot,
              selected: false,
            })) || [],
          inboundDepartureTimeSlots:
            filtersStatus.inboundDepartureTimeSlots?.map((timeSlot) => ({
              ...timeSlot,
              selected: false,
            })) || [],
        };
        return {
          newState,
          event: { type: 'FILTER', origin: 'DEP_TIME_CLEAR_ALL_FILTER' },
        } as FilterChange;
      }
      case 'airlines': {
        const newState = {
          companies:
            filtersStatus.companies?.map((company) => ({
              ...company,
              selected: false,
            })) || [],
          airlines:
            filtersStatus.airlines?.map((airline) => ({
              ...airline,
              selected: false,
            })) || [],
        };
        return {
          newState,
          event: { type: 'FILTER', origin: 'AIRLINES_FILTER' },
        } as CompaniesFilterChange;
      }
      case 'airports': {
        const newState = {
          outboundDepartureAirports:
            filtersStatus.outboundDepartureAirports?.map((airport) => ({
              ...airport,
              selected: false,
            })) || [],
          outboundArrivalAirports:
            filtersStatus.outboundArrivalAirports?.map((airport) => ({
              ...airport,
              selected: false,
            })) || [],
          inboundDepartureAirports:
            filtersStatus.inboundDepartureAirports?.map((airport) => ({
              ...airport,
              selected: false,
            })) || [],
          inboundArrivalAirports:
            filtersStatus.inboundArrivalAirports?.map((airport) => ({
              ...airport,
              selected: false,
            })) || [],
          inboundSameAirport: { on: false },
          outboundSameAirport: { on: false },
          outboundDepartureStation:
            filtersStatus.outboundDepartureStations?.map((station) => ({
              ...station,
              selected: false,
            })) || [],
          outboundArrivalStations:
            filtersStatus.outboundArrivalStations?.map((station) => ({
              ...station,
              selected: false,
            })) || [],
          inboundDepartureStations:
            filtersStatus.inboundDepartureStations?.map((station) => ({
              ...station,
              selected: false,
            })) || [],
          inboundArrivalStation:
            filtersStatus.inboundArrivalStations?.map((station) => ({
              ...station,
              selected: false,
            })) || [],
          inboundSameStation: { on: false },
          outboundSameStation: { on: false },
        };
        return {
          newState,
          event: { type: 'FILTER', origin: 'AIRPORTS_CLEAR_ALL_FILTER' },
        } as FilterChange;
      }
      default:
        return null;
    }
  };

  const clearFilterById = (filterId: FilterPillIdType) => {
    if (!currentFiltersStatus) return;
    const clearChange = buildClearFilterChange(filterId, currentFiltersStatus);
    if (clearChange) changeFilter(clearChange);
  };

  const clearFilter = () => {
    if (!activeFilterModal) {
      closeModal();
      return;
    }
    if (!draftFiltersStatus) return;
    // Footer Reset is staged like any other popover change; Apply confirms it
    const clearChange = buildClearFilterChange(activeFilterModal, draftFiltersStatus);
    if (clearChange) stageFilterChange(clearChange);
  };

  const hasActiveFilterSelectedOptions = (): boolean => {
    if (!activeFilterModal || !draftFiltersStatus) return false;
    return hasFilterSelection(activeFilterModal, draftFiltersStatus);
  };

  const isPillSelected = (filterId: string): boolean => {
    if (!currentFiltersStatus) return false;
    return hasFilterSelection(filterId, currentFiltersStatus);
  };

  const hasFilterSelection = (filterId: string, filtersStatus: TransportFiltersResponse): boolean => {
    switch (filterId) {
      case 'stops':
        return (
          deriveEvoStopsOption({
            outboundStops: filtersStatus.outboundStops,
            inboundStops: filtersStatus.inboundStops,
            directOnly: filtersStatus.directOnly,
          }) !== 'ANY'
        );
      case 'airlines':
        return (
          filtersStatus.airlines?.some((a) => a.selected) || filtersStatus.companies?.some((c) => c.selected) || false
        );
      case 'times':
        return (
          filtersStatus.outboundDepartureTimeSlots?.some((ts) => ts.selected) ||
          filtersStatus.inboundDepartureTimeSlots?.some((ts) => ts.selected) ||
          false
        );
      case 'baggage':
        return (
          filtersStatus.baggageIncluded?.some((b) => b.selected) ||
          filtersStatus.handBaggageIncluded?.some((h) => h.selected) ||
          false
        );
      case 'airports': {
        const hasSelectedAirport = (options: AirportFilter[] | undefined) => options?.some((opt) => opt.selected);
        return (
          hasSelectedAirport(filtersStatus.outboundArrivalAirports) ||
          hasSelectedAirport(filtersStatus.outboundDepartureAirports) ||
          hasSelectedAirport(filtersStatus.inboundArrivalAirports) ||
          hasSelectedAirport(filtersStatus.inboundDepartureAirports) ||
          hasSelectedAirport(filtersStatus.outboundArrivalStations) ||
          hasSelectedAirport(filtersStatus.outboundDepartureStations) ||
          hasSelectedAirport(filtersStatus.inboundArrivalStations) ||
          hasSelectedAirport(filtersStatus.inboundDepartureStations) ||
          filtersStatus.outboundSameAirport?.on ||
          filtersStatus.inboundSameAirport?.on ||
          filtersStatus.outboundSameStation?.on ||
          filtersStatus.inboundSameStation?.on ||
          false
        );
      }
      case 'sort': {
        // MPH-398: sort is "active" (Reset highlighted) when the selected option is not the default
        // (Recommended/BERTOLI), so the Reset button turns active like the other filters.
        const selectedOption = filtersStatus.sortOptions?.find((sf) => sf.selected)?.option;
        return !!selectedOption && sortOptionsMap[selectedOption as keyof typeof sortOptionsMap] !== 'BERTOLI';
      }
      default:
        return false;
    }
  };

  const pillsToRender: PillCandidate[] = filterPillDefinitions.map((pill) => {
    const isActive = isPillSelected(pill.id);

    let mustBeShown = false;
    switch (pill.id) {
      case 'baggage':
        mustBeShown =
          (currentFiltersStatus?.baggageIncluded?.length || 0) > 0 ||
          (currentFiltersStatus?.handBaggageIncluded?.length || 0) > 0;
        break;
      case 'stops':
        mustBeShown =
          (currentFiltersStatus?.outboundStops?.length || 0) > 0 ||
          (currentFiltersStatus?.inboundStops?.length || 0) > 0;
        break;
      case 'times':
        mustBeShown =
          (currentFiltersStatus?.outboundDepartureTimeSlots?.length || 0) > 0 ||
          (currentFiltersStatus?.inboundDepartureTimeSlots?.length || 0) > 0;
        break;
      case 'airports':
        mustBeShown =
          (currentFiltersStatus?.inboundArrivalAirports?.length || 0) > 0 ||
          (currentFiltersStatus?.inboundArrivalStations?.length || 0) > 0 ||
          (currentFiltersStatus?.inboundDepartureAirports?.length || 0) > 0 ||
          (currentFiltersStatus?.inboundDepartureStations?.length || 0) > 0 ||
          (currentFiltersStatus?.outboundArrivalAirports?.length || 0) > 0 ||
          (currentFiltersStatus?.outboundArrivalStations?.length || 0) > 0 ||
          (currentFiltersStatus?.outboundDepartureAirports?.length || 0) > 0 ||
          (currentFiltersStatus?.outboundDepartureStations?.length || 0) > 0 ||
          currentFiltersStatus?.inboundSameAirport?.on ||
          currentFiltersStatus?.inboundSameStation?.on ||
          currentFiltersStatus?.outboundSameAirport?.on ||
          currentFiltersStatus?.outboundSameStation?.on ||
          false;
        break;
      case 'airlines':
        mustBeShown =
          (currentFiltersStatus?.airlines?.length || 0) > 0 || (currentFiltersStatus?.companies?.length || 0) > 0;
        break;
      case 'sort':
        mustBeShown = true;
        break;
      default:
        mustBeShown = false;
        break;
    }

    return {
      label: pill.textKey,
      hasArrow: pill.id === 'sort' || !isActive,
      control: pill.id,
      iconLeft: pill.icon,
      selected: isActive,
      isActive,
      onPillClick: () => togglePillActivation(pill.id),
      onCrossClick: pill.id !== 'sort' || isActive ? () => clearFilterById(pill.id) : undefined,
      brand: localization.brand,
      isOpen: activeFilterModal === pill.id,
      mustBeShown,
      // MPH-398: the rectangular sort pill + separator are the desktop popover tab style; the Evo
      // mobile pills are all fully rounded with no separator (Figma).
      shape: pill.id === 'sort' && isDesktop ? 'rectangular' : undefined,
    };
  });

  const renderActiveFilterModalContent = (): ReactNode => {
    if (!activeFilterModal || !draftFiltersStatus) return null;

    switch (activeFilterModal) {
      case 'baggage':
        return (
          <EvoBaggageFilter
            baggageIncluded={draftFiltersStatus.baggageIncluded}
            handBaggageIncluded={draftFiltersStatus.handBaggageIncluded}
            onChange={stageFilterChange}
          />
        );
      case 'stops':
        return (
          <EvoStopsFilter
            outboundStops={draftFiltersStatus.outboundStops}
            inboundStops={draftFiltersStatus.inboundStops}
            directOnly={draftFiltersStatus.directOnly}
            onChange={stageFilterChange}
          />
        );
      case 'times':
        return (
          <EvoTimeSlotFilter
            outboundTimeSlots={draftFiltersStatus.outboundDepartureTimeSlots}
            inboundTimeSlots={draftFiltersStatus.inboundDepartureTimeSlots}
            onChange={stageFilterChange}
          />
        );
      case 'airports':
        return (
          <EvoAirportsFilter
            inboundArrivalAirports={
              draftFiltersStatus.inboundArrivalAirports || draftFiltersStatus.inboundArrivalStations
            }
            inboundDepartureAirports={
              draftFiltersStatus.inboundDepartureAirports || draftFiltersStatus.inboundDepartureStations
            }
            outboundArrivalAirports={
              draftFiltersStatus.outboundArrivalAirports || draftFiltersStatus.outboundArrivalStations
            }
            outboundDepartureAirports={
              draftFiltersStatus.outboundDepartureAirports || draftFiltersStatus.outboundDepartureStations
            }
            inboundSameAirport={draftFiltersStatus.inboundSameAirport || draftFiltersStatus.inboundSameStation}
            outboundSameAirport={draftFiltersStatus.outboundSameAirport || draftFiltersStatus.outboundSameStation}
            onChange={stageFilterChange}
          />
        );
      case 'airlines':
        return (
          <EvoAirlinesFilter
            companies={draftFiltersStatus.airlines || draftFiltersStatus.companies}
            onChange={stageFilterChange}
          />
        );
      case 'sort':
        return (
          <EvoSortFilter
            sortOptions={draftFiltersStatus.sortOptions}
            onChange={isDesktop ? applySortImmediately : stageFilterChange}
          />
        );
      default:
        return null;
    }
  };

  const activeFilterDefinition = filterPillDefinitions.find((def) => def.id === activeFilterModal);
  const modalTitle = activeFilterDefinition
    ? activeFilterDefinition.id === 'sort'
      ? t('vacation.oss.components.searchresults.sort.sortby')
      : activeFilterDefinition.textKey
    : '';

  // MPH-469: while loading, reuse this component's layout with fixed, non-interactive pills
  // (same shapes, sort separator and spacing) so there is no layout shift when the real filters
  // replace them once the fetch resolves.
  const loadingPills: PillCandidate[] = filterPillDefinitions.map((pill) => ({
    label: pill.textKey,
    hasArrow: true,
    control: pill.id,
    iconLeft: pill.icon,
    selected: false,
    isActive: false,
    brand: localization.brand,
    isOpen: false,
    mustBeShown: true,
    shape: pill.id === 'sort' && isDesktop ? 'rectangular' : undefined,
    // MPH-469: per Figma, all pills (Sort included) are greyed-out/disabled while loading
    disabled: true,
  }));

  const visiblePills = isLoading ? loadingPills : pillsToRender.filter((pillProps) => pillProps.mustBeShown);

  return (
    <PillsRow>
      <ScrollContainer
        style={{ padding: 0, width: '100%' }}
        styleInner={{ padding: '2px var(--evo-pills-inset)' }}
        hideArrows={true}
      >
        {visiblePills.flatMap((pillProps, i) => {
          const isLast = i === visiblePills.length - 1;
          const pillElement = (
            <EvoTransportPill
              key={pillProps.control}
              {...pillProps}
              style={isLast ? {} : { marginRight: 10 }}
              modal={
                isLoading
                  ? undefined
                  : {
                      isOpen: pillProps.isOpen,
                      brand: pillProps.brand,
                      control: pillProps.control,
                      closeModal,
                      applyFilter: applyDraftFilter,
                      modalTitle,
                      hasActiveFilterSelectedOptions,
                      clearFilter,
                      renderActiveFilterModalContent,
                    }
              }
            />
          );
          return pillProps.control === 'sort' && !isLast && isDesktop
            ? [pillElement, <SortPillSeparator key="sort-pill-separator" aria-hidden="true" />]
            : [pillElement];
        })}
      </ScrollContainer>
    </PillsRow>
  );
}
